import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, LinearProgress, Chip, Alert, Collapse, IconButton, Divider } from '@mui/material';
import { TrendingUp as TrendingUpIcon, Assessment as AssessmentIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, UnfoldMore as UnfoldMoreIcon, Visibility as VisibilityIcon, AttachMoney as AttachMoneyIcon, ExpandMore, ExpandLess, KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon, Science as ScienceIcon, PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Category as CategoryIcon, Group as GroupIcon } from '@mui/icons-material';
import { tableStyles, nestedTableStyles } from '../reportingStyles';
import { useReporting } from '../ReportingContext';
import MockChart from '../MockChart';

const AffiliateStatsTab = () => {
  const {
    blogStats,
    blogStatsLoading,
    affiliateStatsData,
  } = useReporting();

  return (
  <Box>
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <GroupIcon color="primary" />
              <Box>
                <Typography variant="h4">{affiliateStatsData.totalAffiliates}</Typography>
                <Typography variant="body2" color="text.secondary">Total Affiliates</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUpIcon color="success" />
              <Box>
                <Typography variant="h4">{affiliateStatsData.activeAffiliates}</Typography>
                <Typography variant="body2" color="text.secondary">Active Affiliates</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AssessmentIcon color="info" />
              <Box>
                <Typography variant="h4">$12,450</Typography>
                <Typography variant="body2" color="text.secondary">Total Commissions</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUpIcon color="warning" />
              <Box>
                <Typography variant="h4">238</Typography>
                <Typography variant="body2" color="text.secondary">Total Conversions</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <MockChart title="Affiliate Signups Over Time" type="bar" />
      </Grid>
      <Grid item xs={12} md={6}>
        <MockChart title="Commission Trends" type="line" />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Top Performing Affiliates</Typography>
            <TableContainer component={Paper} sx={tableStyles.container}>
              <Table>
                <TableHead>
                  <TableRow sx={tableStyles.headerRow}>
                    <TableCell sx={tableStyles.headerCell}>Affiliate</TableCell>
                    <TableCell align="center" sx={tableStyles.headerCell}>Commissions</TableCell>
                    <TableCell align="center" sx={tableStyles.headerCell}>Conversions</TableCell>
                    <TableCell align="center" sx={tableStyles.headerCell}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Derive top performers from the real blogStats payload so the
                    // commissions and conversions in this table match the rest of
                    // Reporting (and the RevShare simulation). Falls back to an
                    // empty state if blogStats hasn't loaded yet.
                    const blogs = blogStats?.pageStatsByBlog || [];
                    const ccByAff = blogStats?.snake?.contentCreatorByAffiliate || {};
                    const grouped = new Map();
                    blogs.forEach(b => {
                      const id = b.affiliateId || 'unknown';
                      if (!grouped.has(id)) {
                        grouped.set(id, {
                          id,
                          name: b.affiliateName || b.affiliateUsername || id,
                          revenue: 0,
                          sales: 0
                        });
                      }
                      const g = grouped.get(id);
                      g.revenue += b.totalRevenue || 0;
                      g.sales += b.sales || 0;
                    });
                    Object.entries(ccByAff).forEach(([affId, cc]) => {
                      if (!grouped.has(affId)) {
                        grouped.set(affId, { id: affId, name: affId, revenue: 0, sales: 0 });
                      }
                      const g = grouped.get(affId);
                      g.revenue += cc.revenue || 0;
                      g.sales += cc.sales || 0;
                    });
                    const top = Array.from(grouped.values())
                      .sort((a, b) => b.revenue - a.revenue)
                      .slice(0, 5);

                    if (top.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ ...tableStyles.bodyCell, color: '#64748b' }}>
                            {blogStatsLoading ? 'Loading…' : 'No affiliate activity in this period'}
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return top.map((affiliate, index) => (
                      <TableRow
                        key={affiliate.id}
                        sx={index % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}
                      >
                        <TableCell sx={tableStyles.bodyCell}>{affiliate.name}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>${affiliate.revenue.toFixed(2)}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>{affiliate.sales}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          <Chip label="Active" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>

  );
};

export default AffiliateStatsTab;
